import styled, { css } from 'styled-components';

import { size } from '../../layout/helpers';
import { noTransform } from '../../layout/keyframes';

export const PageLayoutMain = styled.div`
  ${(props) => props.theme.breakpoint.LtMd} {
    display: contents;
  }

  ${(props) => props.theme.breakpoint.Md} {
    grid-column: 1 / 2;
    display: flex;
    flex-direction: column;
    gap: var(--main-gutter);
  }
`;

export const PageLayoutAside = styled.div`
  ${(props) => props.theme.breakpoint.LtMd} {
    display: contents;
  }

  ${(props) => props.theme.breakpoint.Md} {
    position: sticky;
    top: 0;
    height: max-content;
    grid-column: 2 / 3;
    display: flex;
    flex-direction: column;
    gap: ${size(2)};
  }
`;

export const PageLayout = styled.div`
  ${(props) => props.theme.breakpoint.LtMd} {
    display: flex;
    flex-direction: column;
    margin-bottom: var(--main-gutter-negative);
  }

  ${(props) => props.theme.breakpoint.Md} {
    --page-layout-gap: var(--main-gutter);

    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    gap: var(--page-layout-gap);
  }
`;

interface PageLayoutSectionProps {
  $order?: number; // Order used on smaller screens (LtMd)
  $variant?: 'header' | 'roadmap' | 'asideCta' | 'mainCta' | 'stepNavigation' | 'tableOfContents';
  $tone?: 'primary';
}

export const PageLayoutSection = styled.div<PageLayoutSectionProps>`
  // Order used on smaller screens (LtMd)
  --order-roadmap: var(--order-override, -100);
  --order-table-of-contents: var(--order-override, -90);
  --order-header: var(--order-override, -80);
  --order-step-navigation: var(--order-override, 90);
  --order-main-cta: var(--order-override, 100);

  ${(props) => props.theme.breakpoint.LtMd} {
    order: var(--order-override, var(--order));
    margin: 0 var(--main-gutter-negative);
    padding: var(--main-gutter);
    border-top: 1px solid var(--c-border-main);
  }

  // Add border if placed in Aside
  ${PageLayoutAside} & {
    ${(props) => props.theme.breakpoint.LtMd} {
      --border-width: 1px;

      position: relative;
      padding: var(
        --aside-section-padding-mobile-override,
        calc(var(--main-gutter) * 2 + var(--border-width))
      );

      &::before {
        content: var(--aside-section-content-mobile-override, '');
        position: absolute;
        inset: var(--main-gutter);
        border: var(--border-width) solid var(--c-border-main);
        pointer-events: none;
      }
    }

    ${(props) => props.theme.breakpoint.Md} {
      padding: ${size(2)};
      border: var(--aside-section-border-desktop-override, 1px solid var(--c-border-main));
      border-radius: ${size(1)};
    }
  }

  ${({ $order }) =>
    $order &&
    css`
      --order-override: ${$order};
    `}

  ${({ $tone }) =>
    $tone === 'primary'
      ? css`
          background-color: var(--c-bg-primary);
        `
      : undefined}

  // Variants
  ${({ $variant }) =>
    $variant === 'header'
      ? css`
          --order: var(--order-header);
        `
      : $variant === 'roadmap'
        ? css`
            --order: var(--order-roadmap);
            --aside-section-border-desktop-override: none;
            --aside-section-content-mobile-override: none;
            --aside-section-padding-mobile-override: var(--main-gutter);
          `
        : $variant === 'asideCta'
          ? css`
              --aside-section-border-desktop-override: none;
            `
          : $variant === 'mainCta'
            ? css`
                --order: var(--order-main-cta);

                ${(props) => props.theme.breakpoint.LtMd} {
                  position: sticky;
                  bottom: 0;
                  z-index: ${(props) => props.theme.zindex.pageLayoutSticky};
                  background-color: var(--c-bg-main);
                  transform: translateY(100%);
                  animation: ${noTransform} 200ms ease forwards;
                }

                ${(props) => props.theme.breakpoint.Md} {
                  display: none; // TODO: Create a floating element from asideCta instead
                }
              `
            : $variant === 'stepNavigation'
              ? css`
                  --order: var(--order-step-navigation);
                `
              : $variant === 'tableOfContents'
                ? css`
                    --order: var(--order-table-of-contents);
                    --aside-section-border-desktop-override: none;
                    --aside-section-content-mobile-override: none;
                    --aside-section-padding-mobile-override: var(--main-gutter);
                  `
                : undefined}
`;
